/**
 * Compose the full newsletter email HTML from its sections — extracted from the
 * OliveNewsletterBuilder orchestrator. This is the actual document that gets
 * sent: the Outlook-safe `<!DOCTYPE>`/head/body chrome + the centered 650px
 * container, with each section dispatched to its own `*ToHtml` builder in
 * order. Pure (sections + tenant meta in → string out), so the composition,
 * ordering, and tenant-threading are unit-testable apart from the editor UI.
 *
 * No money / reseller here — wall-neutral.
 */
import type { NewsletterSection, TenantInfo } from "./stock-images"
import { headerToHtml } from "./header-section"
import { introducingToHtml } from "./introducing-section"
import { caseStudyToHtml } from "./case-study-section"
import { companyNewsToHtml } from "./company-news-section"
import { footerToHtml } from "./footer-section"

/** Tenant fields threaded into the header + footer of the composed email. */
export interface NewsletterRenderMeta {
  tenantName: string
  tenantLogo?: string | null | undefined
  tenantInfo?: TenantInfo | undefined
  year: number
}

/** Render the sections into the complete, send-ready newsletter email HTML. */
export function generateEmailHTML(
  sections: NewsletterSection[],
  meta: NewsletterRenderMeta,
): string {
  const { tenantName, tenantLogo, tenantInfo, year } = meta

  let html = `
<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>Newsletter</title>
  <!--[if gte mso 9]>
  <xml>
    <o:OfficeDocumentSettings>
      <o:AllowPNG/>
      <o:PixelsPerInch>96</o:PixelsPerInch>
    </o:OfficeDocumentSettings>
  </xml>
  <![endif]-->
  <style type="text/css">
    body { margin: 0; padding: 0; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table { border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    img { border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic; }
  </style>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f4f4f4;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table width="650" cellpadding="0" cellspacing="0" border="0" style="background-color: #ffffff;">
`

  sections.forEach((section) => {
    switch (section.type) {
      case "header":
        html += headerToHtml(section, tenantName, tenantLogo, tenantInfo)
        break
      case "introducing":
        html += introducingToHtml(section)
        break
      case "case_study":
        html += caseStudyToHtml(section)
        break
      case "company_news":
        html += companyNewsToHtml(section)
        break
      case "footer":
        html += footerToHtml(section, tenantName, tenantInfo, year)
        break
    }
  })

  html += `
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
`
  return html
}
